import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import CouponBanner from "./CouponBanner";
import BusinessActionRow from "./BusinessActionRow";
import BusinessPhotoGallery, {
  normalizeBusinessPhotos,
} from "./BusinessPhotoGallery";
import ComingSoonDirectory from "./ComingSoonDirectory";

import {
  CATEGORY_META,
  normalizeCategoryValue,
  resolveCategoryMeta,
} from "./categoryDirectoryConfig";

import { resolveCommunityMeta } from "./communityDirectoryConfig";

import CommunityShowcase from "./CommunityShowcase";
import "flag-icons/css/flag-icons.min.css";
import "./CommunityShowcase.css";
import "./VisitResturants.css";
import "./DirectorySearch.css";
import "./BusinessSearchDeepLink.css";

const configuredApiBase =
  typeof process !== "undefined" && process.env
    ? process.env.REACT_APP_GOOURHUB_BUSINESS_API
    : "";

const API_BASE_URL = (
  configuredApiBase || "https://business-api.goourhub.com"
).replace(/\/+$/, "");

const PAGE_LIMIT = 200;
const MAX_TOTAL_PAGES = 100;

const CATEGORY_ORDER = new Map(
  CATEGORY_META.map((category, index) => [category.value, index])
);

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const cleanStringList = (...values) => {
  const output = [];
  const seen = new Set();

  values.forEach((value) => {
    const items = Array.isArray(value)
      ? value
      : typeof value === "string" && value.trim()
        ? [value]
        : [];

    items.forEach((item) => {
      const clean = String(item || "").trim();
      const key = clean.toLowerCase();

      if (!clean || seen.has(key)) {
        return;
      }

      seen.add(key);
      output.push(clean);
    });
  });

  return output;
};

const parseDetails = (business) => {
  if (
    business?.details &&
    typeof business.details === "object" &&
    !Array.isArray(business.details)
  ) {
    return business.details;
  }

  if (
    business?.details_json &&
    typeof business.details_json === "object" &&
    !Array.isArray(business.details_json)
  ) {
    return business.details_json;
  }

  if (typeof business?.details_json === "string") {
    try {
      const parsed = JSON.parse(business.details_json);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.warn("Unable to parse business details_json.", error);
    }
  }

  return {};
};

const buildLocationAddress = (details) => {
  if (!Array.isArray(details?.locations) || details.locations.length === 0) {
    return "";
  }

  const location = details.locations[0];

  if (typeof location === "string") {
    return location.trim();
  }

  if (!location || typeof location !== "object") {
    return "";
  }

  const directAddress = firstText(
    location.full_address,
    location.fullAddress,
    location.address
  );

  if (directAddress) {
    return directAddress;
  }

  return [
    firstText(location.street_address, location.streetAddress, location.street),
    firstText(location.city),
    firstText(location.state),
    firstText(location.zip_code, location.zipCode, location.zip),
  ]
    .filter(Boolean)
    .join(", ");
};

const normalizeExternalUrl = (value) => {
  const cleanValue = firstText(value);

  if (!cleanValue) {
    return "";
  }

  if (/^https?:\/\//i.test(cleanValue)) {
    return cleanValue;
  }

  return `https://${cleanValue}`;
};

const normalizeBusiness = (business) => {
  const details = parseDetails(business);
  const photoData = normalizeBusinessPhotos(business, details);

  const primaryCategory =
    normalizeCategoryValue(
      firstText(business?.category, details.category, details.business_type)
    ) || "business";

  const categoryValues = cleanStringList(
    primaryCategory,
    business?.category_tags,
    business?.categoryTags,
    details.category_tags,
    details.categoryTags
  )
    .map((value) => normalizeCategoryValue(value))
    .filter(Boolean);

  const uniqueCategoryValues = Array.from(new Set(categoryValues));
  const primaryCategoryMeta = resolveCategoryMeta(primaryCategory);

  const profilePath = firstText(
    business?.profile_path,
    business?.profilePath,
    details.profile_path,
    details.profilePath,
    details.legacy_profile
  );

  const website = normalizeExternalUrl(
    firstText(business?.website, business?.url, details.website, details.url)
  );

  const source = firstText(business?.source, details.source).toLowerCase();
  const listingPlan = firstText(
    business?.listing_plan,
    business?.listingPlan,
    details.listing_plan,
    details.listingPlan
  ).toLowerCase();
  const paymentStatus = firstText(
    business?.payment_status,
    business?.paymentStatus,
    details.payment_status,
    details.paymentStatus
  ).toLowerCase();

  const isPremium =
    ["premium", "featured", "paid"].includes(listingPlan) ||
    ["paid", "complete", "completed", "succeeded"].includes(paymentStatus) ||
    Boolean(details.legacy_premium_label);

  const isOwnerSubmitted =
    Boolean(business?.email_verified_at || business?.emailVerifiedAt) ||
    source.includes("partner") ||
    source.includes("add business") ||
    source.includes("owner");

  const specialties = cleanStringList(
    business?.specialties,
    details.specialties,
    details.source_categories,
    details.sourceCategories
  );

  const communities = cleanStringList(
    business?.communities,
    details.communities
  );

  const languages = cleanStringList(business?.languages, details.languages);

  const normalized = {
    ...business,
    id: firstText(business?.id, details.canonical_id) || business?.name,
    name: firstText(business?.name, details.name) || "Business",
    category: primaryCategory,
    categoryValues:
      uniqueCategoryValues.length > 0
        ? uniqueCategoryValues
        : [primaryCategoryMeta.value],
    categoryMeta: primaryCategoryMeta,
    icon: firstText(business?.icon, details.icon) || primaryCategoryMeta.icon,
    specialties,
    communities,
    languages,
    address: firstText(
      business?.address,
      business?.full_address,
      business?.fullAddress,
      details.address,
      details.full_address,
      details.fullAddress,
      buildLocationAddress(details)
    ),
    phone: firstText(
      business?.phone_number,
      business?.phoneNumber,
      business?.phone,
      details.phone_number,
      details.phoneNumber,
      details.phone
    ),
    website,
    mapUrl: firstText(
      business?.map_url,
      business?.mapUrl,
      details.map_url,
      details.mapUrl
    ),
    photos: photoData.photos,
coverPhoto: photoData.coverPhoto,

    profilePath,
    isPremium,
    isOwnerSubmitted,
  };

  normalized.searchText = [
    normalized.name,
    normalized.address,
    normalized.phone,
    normalized.category,
    normalized.categoryMeta.label,
    normalized.specialties.join(" "),
    normalized.communities.join(" "),
    normalized.languages.join(" "),
  ]
    .join(" ")
    .toLowerCase();

  return normalized;
};

const buildMapUrl = (item) =>
  item.mapUrl ||
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    item.address || item.name || ""
  )}`;

const fetchCommunityPage = async (communityValue, page, signal) => {
  const requestUrl =
    `${API_BASE_URL}/api/businesses` +
    `?community=${encodeURIComponent(communityValue)}` +
    `&page=${page}` +
    `&limit=${PAGE_LIMIT}` +
    "&sort=name";

  const response = await fetch(requestUrl, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });

  let payload;

  try {
    payload = await response.json();
  } catch {
    throw new Error("The business directory returned an invalid response.");
  }

  if (!response.ok || payload?.success === false) {
    throw new Error(
      payload?.message || `Unable to load businesses (${response.status}).`
    );
  }

  if (!Array.isArray(payload?.businesses)) {
    throw new Error("The business directory did not return a business list.");
  }

  return payload;
};

const matchesQuery = (business, rawQuery) => {
  const query = String(rawQuery || "").trim().toLowerCase();
  return !query || business.searchText.includes(query);
};

const groupBusinessesByCategory = (businesses) => {
  const groups = new Map();

  businesses.forEach((business) => {
    const values =
      business.categoryValues?.length > 0
        ? business.categoryValues
        : [business.category || "business"];

    values.forEach((value) => {
      const meta = resolveCategoryMeta(value);

      if (!groups.has(meta.value)) {
        groups.set(meta.value, {
          meta,
          businesses: [],
          seenBusinessIds: new Set(),
        });
      }

      const group = groups.get(meta.value);
      const businessKey = business.id || `${business.name}|${business.address}`;

      if (!group.seenBusinessIds.has(businessKey)) {
        group.seenBusinessIds.add(businessKey);
        group.businesses.push(business);
      }
    });
  });

  return Array.from(groups.values())
    .map(({ meta, businesses: groupedBusinesses }) => ({
      meta,
      businesses: groupedBusinesses.sort((left, right) =>
        left.name.localeCompare(right.name, undefined, {
          sensitivity: "base",
        })
      ),
    }))
    .sort((left, right) => {
      const leftOrder = CATEGORY_ORDER.has(left.meta.value)
        ? CATEGORY_ORDER.get(left.meta.value)
        : Number.MAX_SAFE_INTEGER;
      const rightOrder = CATEGORY_ORDER.has(right.meta.value)
        ? CATEGORY_ORDER.get(right.meta.value)
        : Number.MAX_SAFE_INTEGER;

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      return left.meta.label.localeCompare(right.meta.label, undefined, {
        sensitivity: "base",
      });
    });
};

function BusinessCard({
  item,
  onOpenDetails,
  navigate,
  isSearchTarget = false,
}) {
  const internalProfile = item.profilePath?.startsWith("/");
  const mapHref = item.address || item.mapUrl ? buildMapUrl(item) : "";
  const specialties = item.specialties.slice(0, 3);

  return (
    <article
      className={`new-tile visit-card ${
        item.isPremium ? "visit-card--premium" : ""
      }${isSearchTarget ? " is-search-target" : ""}`}
      data-business-id={item.id}
      tabIndex={-1}
    >
      {item.isPremium && (
        <div className="top-ribbon">
          <span>Premium</span>
        </div>
      )}
      {item.coverPhoto && (
  <img
    className="visit-card__cover"
    src={item.coverPhoto}
    alt={`${item.name} cover`}
    loading="lazy"
  />
)}

      <span className="icon-for-new-tile" aria-hidden="true">
        {item.icon}
      </span>

      <h3 className="tile-name visit-card__title">
        {item.name}
        {!item.isOwnerSubmitted && (
          <span className="info-badge" title="Unverified listing">
            ⓘ
          </span>
        )}
      </h3>

      {specialties.length > 0 && (
        <div className="visit-card__chips">
          {specialties.map((specialty) => (
            <span key={specialty} className="visit-chip">
              {specialty}
            </span>
          ))}
        </div>
      )}

      {item.address && <p className="visit-card__address">{item.address}</p>}
      {item.phone && <p className="visit-card__phone">{item.phone}</p>}

      <div className="visit-card__actions">
        <button
          type="button"
          className="visit-card__btn"
          onClick={() =>
            internalProfile ? navigate(item.profilePath) : onOpenDetails(item)
          }
        >
          {internalProfile ? "Open page" : "View details"}
        </button>

        {mapHref && (
          <a
            href={mapHref}
            target="_blank"
            rel="noopener noreferrer"
            className="visit-card__btn visit-card__btn--secondary"
          >
            Map
          </a>
        )}
      </div>

      {/* Save / Share controls for this community business */}
      <div
        style={{
          marginTop: 12,
          position: "relative",
          zIndex: 2,
        }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <BusinessActionRow
          businessId={item.id}
          businessName={item.name}
          compact
        />
      </div>
    </article>
  );
}

function CategorySection({
  section,
  searchQuery,
  onOpenDetails,
  navigate,
  highlightedBusinessId,
}) {
  const { meta, businesses } = section;

  return (
    <section id={`community-category-${meta.slug}`} className="category-section">
      <div className="category-section__head">
        <div>
          <span className="category-section__eyebrow">Business type</span>
          <div className="category-header-row">
            <h2 className="category-header">
              {meta.icon} {meta.label}
            </h2>
            <span className="category-count">
              {businesses.length}{" "}
              {businesses.length === 1 ? "listing" : "listings"}
            </span>
          </div>
          
        </div>
      </div>

      {businesses.length > 0 ? (
        <div className="new-tile-container-new">
          {businesses.map((business, index) => (
            <BusinessCard
              key={business.id || `${business.name}-${index}`}
              item={business}
              onOpenDetails={onOpenDetails}
              navigate={navigate}
              isSearchTarget={
                String(business.id) === String(highlightedBusinessId)
              }
            />
          ))}
        </div>
      ) : (
        <div className="visit-empty">
          {searchQuery ? (
            <>
              No results matched <strong>{searchQuery}</strong> in this category.
            </>
          ) : (
            "No available listings are available in this category yet."
          )}
        </div>
      )}
    </section>
  );
}

function InfoModal({ item, communitySlug, onClose }) {
  if (!item) {
    return null;
  }

  const dialogTitleId = `business-popup-${communitySlug}`;
  const mapHref = item.address || item.mapUrl ? buildMapUrl(item) : "";

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="popup-box__head">
          <div>
            <span className="popup-box__eyebrow">
              {item.categoryMeta.icon} {item.categoryMeta.label}
            </span>
            <h3 id={dialogTitleId}>{item.name}</h3>
          </div>

          <button
            type="button"
            className="popup-close"
            onClick={onClose}
            aria-label="Close details"
          >
            ×
          </button>
          <BusinessPhotoGallery
  photos={item.photos}
  coverPhoto={item.coverPhoto}
  businessName={item.name}
/>

        </div>

        {item.specialties.length > 0 && (
          <div className="popup-box__section">
            <strong>Specialties</strong>
            <p>{item.specialties.join(" • ")}</p>
          </div>
        )}

        {item.address && (
          <div className="popup-box__section">
            <strong>Address</strong>
            <p>{item.address}</p>
          </div>
        )}

        {item.phone && (
          <div className="popup-box__section">
            <strong>Phone</strong>
            <p>{item.phone}</p>
          </div>
        )}

        <div className="popup-box__actions">
          {item.website && (
            <a
              href={item.website}
              target="_blank"
              rel="noopener noreferrer"
              className="visit-card__btn"
            >
              Visit website
            </a>
          )}

          {mapHref && (
            <a
              href={mapHref}
              target="_blank"
              rel="noopener noreferrer"
              className="visit-card__btn visit-card__btn--secondary"
            >
              Take me there
            </a>
          )}
        </div>

        {/* Full Save / Share row for the selected community business */}
        <div style={{ margin: "14px 0 8px" }}>
          <BusinessActionRow
            businessId={item.id}
            businessName={item.name}
          />
        </div>

        {!item.isOwnerSubmitted && (
          <>
            <hr className="popup-divider" />
            <p className="disclaimer-text">
              Directory listing only. Details may come from public sources and
              should be confirmed directly with the business.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Reusable database-driven community page.
 *
 * Dynamic route:
 *   /communities/indian
 *   /communities/puerto-rican
 *
 * Legacy route usage:
 *   <BusinessCommunityPage communityValue="Indian" />
 */
function BusinessCommunityPage({
  communityValue = "",
  title = "",
  showCouponBanner = true,
}) {
  const { communitySlug = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetBusinessId = searchParams.get("business") || "";
  const requestedCommunity = communityValue || communitySlug;

  const communityMeta = useMemo(() => {
    const resolved = resolveCommunityMeta(requestedCommunity);

    return {
      ...resolved,
      label: title || resolved.label,
    };
  }, [requestedCommunity, title]);

  const [businesses, setBusinesses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [highlightedBusinessId, setHighlightedBusinessId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [reloadNumber, setReloadNumber] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const loadBusinesses = async () => {
      setLoading(true);
      setErrorMessage("");
      setBusinesses([]);
      setSelected(null);
      setHighlightedBusinessId("");
      setSearchQuery("");

      try {
        const firstPage = await fetchCommunityPage(
          communityMeta.value,
          1,
          controller.signal
        );

        const reportedTotalPages =
          Number(firstPage?.pagination?.totalPages) || 1;
        const totalPages = Math.min(
          MAX_TOTAL_PAGES,
          Math.max(1, reportedTotalPages)
        );

        const allBusinesses = [...firstPage.businesses];

        for (let page = 2; page <= totalPages; page += 1) {
          const pagePayload = await fetchCommunityPage(
            communityMeta.value,
            page,
            controller.signal
          );
          allBusinesses.push(...pagePayload.businesses);
        }

        const uniqueBusinesses = new Map();

        allBusinesses.forEach((business, index) => {
          const fallbackKey = [
            business?.name,
            business?.address,
            business?.phone_number,
            index,
          ].join("|");

          uniqueBusinesses.set(business?.id || fallbackKey, business);
        });

        const normalizedBusinesses = Array.from(uniqueBusinesses.values())
          .map(normalizeBusiness)
          .sort((left, right) =>
            left.name.localeCompare(right.name, undefined, {
              sensitivity: "base",
            })
          );

        if (!controller.signal.aborted) {
          setBusinesses(normalizedBusinesses);
        }
      } catch (error) {
        if (error.name !== "AbortError" && !controller.signal.aborted) {
          console.error(
            `Unable to load ${communityMeta.value} businesses.`,
            error
          );
          setErrorMessage(
            error.message || "Unable to load businesses. Please try again."
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadBusinesses();

    return () => controller.abort();
  }, [communityMeta.value, reloadNumber]);

  const focusBusiness = (business, { updateUrl = false } = {}) => {
    if (!business) {
      return;
    }

    setHighlightedBusinessId(String(business.id));

    if (updateUrl) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("business", String(business.id));
      setSearchParams(nextParams, { replace: true });
    }

    const scrollTimer = window.setTimeout(() => {
      const targetElement = Array.from(
        document.querySelectorAll("[data-business-id]")
      ).find(
        (element) =>
          String(element.getAttribute("data-business-id")) ===
          String(business.id)
      );

      targetElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      if (typeof targetElement?.focus === "function") {
        try {
          targetElement.focus({ preventScroll: true });
        } catch {
          targetElement.focus();
        }
      }
    }, 80);

    const modalTimer = window.setTimeout(() => setSelected(business), 600);
    const highlightTimer = window.setTimeout(
      () => setHighlightedBusinessId(""),
      4200
    );

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(modalTimer);
      window.clearTimeout(highlightTimer);
    };
  };

  useEffect(() => {
    if (!targetBusinessId || loading || businesses.length === 0) {
      return undefined;
    }

    const targetBusiness = businesses.find(
      (business) => String(business.id) === String(targetBusinessId)
    );

    if (!targetBusiness) {
      return undefined;
    }

    setSearchQuery("");
    return focusBusiness(targetBusiness);
    // focusBusiness intentionally uses current page state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetBusinessId, loading, businesses]);

  useEffect(() => {
    if (!selected) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSelected(null);
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [selected]);

  const allSections = useMemo(
    () => groupBusinessesByCategory(businesses),
    [businesses]
  );

  const filteredBusinesses = useMemo(
    () => businesses.filter((business) => matchesQuery(business, searchQuery)),
    [businesses, searchQuery]
  );

  const filteredSections = useMemo(
    () => groupBusinessesByCategory(filteredBusinesses),
    [filteredBusinesses]
  );

  const premiumCount = businesses.filter((business) => business.isPremium).length;
  const stats = [
    { label: "Businesses", value: businesses.length },
    { label: "Categories", value: allSections.length },
    premiumCount > 0 ? { label: "Premium", value: premiumCount } : null,
  ].filter(Boolean);

  const navigationSections = searchQuery ? filteredSections : allSections;

  return (
    <div className="visit-resturants-page">
      <div className="categories-container">
        {loading && (
          <section className="category-section">
            <h2 className="category-header">{communityMeta.label}</h2>
            <p role="status">Loading businesses...</p>
          </section>
        )}

     {!loading &&
  !errorMessage &&
  businesses.length === 0 && (
    <ComingSoonDirectory
      name={communityMeta.label}
      kind="community"
      tags={[
        "Restaurants",
        "Grocery stores",
        "Local services",
      ]}
    />
  )}

        {!loading && !errorMessage && businesses.length === 0 && (
          <section className="category-section">
            <h2 className="category-header">{communityMeta.label}</h2>
            
          </section>
        )}

        {!loading && !errorMessage && businesses.length > 0 && (
          <>
            {showCouponBanner && <CouponBanner />}

            <CommunityShowcase
              title={communityMeta.label}
              icon={
                communityMeta.flag ? (
                  <span
                    className={`fi fi-${String(communityMeta.flag).toLowerCase()} ci-hero__flagimg`}
                    aria-hidden="true"
                  />
                ) : (
                  communityMeta.icon || "🌍"
                )
              }
              businesses={businesses}
              loading={false}
              error=""
              communitySlug={communityMeta.value}
              onSelectBusiness={(business) => focusBusiness(business, { updateUrl: true })}
            />
          </>
        )}

        <div className="back-home-tile">
          <Link to="/hub" className="back-home-link">
            Back to Hub
          </Link>
          <Link to="/" className="back-home-link back-home-link--secondary">
            Homepage
          </Link>
        </div>

        <InfoModal
          item={selected}
          communitySlug={communityMeta.slug}
          onClose={() => {
            setSelected(null);

            if (searchParams.has("business")) {
              const nextParams = new URLSearchParams(searchParams);
              nextParams.delete("business");
              setSearchParams(nextParams, { replace: true });
            }
          }}
        />
      </div>
    </div>
  );
}

export default BusinessCommunityPage;
